import { useState, useEffect } from "react";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface EmailPreferences {
  id?: string;
  onboarding_emails: boolean;
  waitlist_emails: boolean;
  admin_emails: boolean;
  digest_emails: boolean;
  content_recommendations: boolean;
  marketing_emails: boolean;
  digest_frequency: 'daily' | 'weekly' | 'monthly' | 'never';
  recommendation_frequency: 'weekly' | 'biweekly' | 'monthly' | 'never';
}

const DEFAULT_PREFERENCES: EmailPreferences = {
  onboarding_emails: true,
  waitlist_emails: true,
  admin_emails: true,
  digest_emails: true,
  content_recommendations: true,
  marketing_emails: false,
  digest_frequency: 'weekly',
  recommendation_frequency: 'weekly',
};

export const useEmailPreferences = () => {
  const [preferences, setPreferences] = useState<EmailPreferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("email_preferences")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setPreferences(data as EmailPreferences);
        } else {
          // Create default preferences if none exist
          const { data: newPrefs, error: insertError } = await supabase
            .from("email_preferences")
            .insert([{ user_id: user.id, ...DEFAULT_PREFERENCES }])
            .select()
            .single();

          if (insertError) throw insertError;
          if (newPrefs) setPreferences(newPrefs as EmailPreferences);
        }
      } catch (error) {
        console.error("Error fetching email preferences:", error);
        toast.error("Failed to load email preferences");
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [user]);

  const updatePreferences = async (updates: Partial<EmailPreferences>) => {
    if (!user) return;

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("email_preferences")
        .update(updates)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPreferences(data as EmailPreferences);
        toast.success("Preferences updated");
      }
    } catch (error) {
      console.error("Error updating email preferences:", error);
      toast.error("Failed to update preferences");
    } finally {
      setSaving(false);
    }
  };

  return {
    preferences,
    loading,
    saving,
    updatePreferences,
  };
};
